import {CommonModule, isPlatformBrowser} from '@angular/common';
import {booleanAttribute, Component, computed, inject, Input, model, OnInit, PLATFORM_ID, Signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {$t, updatePreset, updateSurfacePalette} from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import Lara from '@primeng/themes/lara';
import {PrimeNG} from 'primeng/config';
import {SelectButtonModule} from 'primeng/selectbutton';
import {LayoutService} from '@/layout/service/layout.service';
import {Router} from '@angular/router';
import {DrawerModule} from 'primeng/drawer';
import {ToggleSwitchModule} from 'primeng/toggleswitch';
import {RadioButtonModule} from 'primeng/radiobutton';
import { ConfigurationService } from '../service/configuration.service';

const presets = {
    Aura,
    Lara
} as const;

declare type KeyOfType<T> = keyof T extends infer U ? U : never;

declare type SurfacesType = {
    name?: string;
    palette?: {
        0?: string;
        50?: string;
        100?: string;
        200?: string;
        300?: string;
        400?: string;
        500?: string;
        600?: string;
        700?: string;
        800?: string;
        900?: string;
        950?: string;
    };
};

@Component({
    selector: 'app-configurator',
    standalone: true,
    imports: [CommonModule, FormsModule, SelectButtonModule, ToggleSwitchModule, RadioButtonModule],
    template: `
            <div class="flex flex-col gap-4">
                <div>
                    <span class="text-lg font-semibold">Primary</span>
                    <div class="pt-2 flex gap-2 flex-wrap">
                        @for (primaryColor of primaryColors(); track primaryColor.name) {
                            <button
                                type="button"
                                [title]="primaryColor.name"
                                (click)="updateColors($event, 'primary', primaryColor)"
                                [ngClass]="{
                                    'outline-primary': primaryColor.name === selectedPrimaryColor()
                                }"
                                class="cursor-pointer w-6 h-6 rounded-full flex flex-shrink-0 items-center justify-center p-0 outline-none outline-offset-1"
                                [style]="{
                                    'background-color': primaryColor?.name === 'noir' ? 'var(--text-color)' : primaryColor?.palette?.['500']
                                }"
                            ></button>
                        }
                    </div>
                </div>

                <!-- <div>
                    <span class="text-lg font-semibold">Surface</span>
                    <div class="pt-2 flex gap-2 flex-wrap">
                        @for (surface of surfaces; track surface.name) {
                            <button
                                type="button"
                                [title]="surface.name"
                                (click)="updateColors($event, 'surface', surface)"
                                class="cursor-pointer w-6 h-6 rounded-full flex flex-shrink-0 items-center justify-center p-0 outline-none outline-offset-1"
                                [ngClass]="{
                                    'outline-primary': selectedSurface() ? selectedSurface() === surface.name : darkTheme() ? surface.name === 'zinc' : surface.name === 'slate'
                                }"
                                [style]="{
                                    'background-color': surface?.palette?.['500']
                                }"
                            ></button>
                        }
                    </div>
                </div> -->

                <!-- <div class="flex flex-col gap-2">
                    <span class="text-lg font-semibold">Presets</span>
                    <p-selectbutton [options]="presets" [ngModel]="selectedPreset()" (ngModelChange)="onPresetChange($event)" [allowEmpty]="false"></p-selectbutton>
                </div> -->

                <div class="flex flex-col gap-2">
                    <span class="text-lg font-semibold">Color Scheme</span>
                    <p-selectbutton [ngModel]="darkTheme()" (ngModelChange)="toggleDarkMode()" [options]="themeOptions" optionLabel="name" optionValue="value" [allowEmpty]="false"></p-selectbutton>
                </div>

                <div class="flex flex-col gap-2">
                    <span class="text-lg font-semibold">Menu Type</span>
                    <div class="flex flex-wrap flex-col gap-3">
                        <div class="flex">
                            <div class="flex items-center gap-2 w-1/2">
                                <p-radio-button name="menuMode" value="static" [(ngModel)]="menuMode" (ngModelChange)="onMenuModeChange('static')" inputId="static"></p-radio-button>
                                <label for="static">Static</label>
                            </div>

                            <div class="flex items-center gap-2 w-1/2">
                                <p-radio-button name="menuMode" value="overlay" [(ngModel)]="menuMode" (ngModelChange)="onMenuModeChange('overlay')" inputId="overlay"></p-radio-button>
                                <label for="overlay">Overlay</label>
                            </div>
                        </div>
                        <div class="flex">
                            <div class="flex items-center gap-2 w-1/2">
                                <p-radio-button name="menuMode" value="slim" [(ngModel)]="menuMode" (ngModelChange)="onMenuModeChange('slim')" inputId="slim"></p-radio-button>
                                <label for="slim">Slim</label>
                            </div>
                            <div class="flex items-center gap-2 w-1/2">
                                <p-radio-button name="menuMode" value="slim-plus" [(ngModel)]="menuMode" (ngModelChange)="onMenuModeChange('slim-plus')" inputId="slim-plus"></p-radio-button>
                                <label for="slim-plus">Slim+</label>
                            </div>
                        </div>
                        <div class="flex">
                            <div class="flex items-center gap-2 w-1/2">
                                <p-radio-button name="menuMode" value="reveal" [(ngModel)]="menuMode" (ngModelChange)="onMenuModeChange('reveal')" inputId="reveal"></p-radio-button>
                                <label for="reveal">Reveal</label>
                            </div>
                            <div class="flex items-center gap-2 w-1/2">
                                <p-radio-button name="menuMode" value="drawer" [(ngModel)]="menuMode" (ngModelChange)="onMenuModeChange('drawer')" inputId="drawer"></p-radio-button>
                                <label for="drawer">Drawer</label>
                            </div>
                        </div>
                        <div class="flex items-center gap-2 w-1/2">
                            <p-radio-button name="menuMode" value="horizontal" [(ngModel)]="menuMode" (ngModelChange)="onMenuModeChange('horizontal')" inputId="horizontal"></p-radio-button>
                            <label for="horizontal">Horizontal</label>
                        </div>
                    </div>
                </div>

                <!-- <div *ngIf="!simple" class="flex flex-col gap-2">
                    <span class="text-lg font-semibold">Menu Theme</span>
                    <div class="flex gap-4">
                        <div class="flex items-center gap-2">
                            <p-radio-button name="menuTheme" value="light" [ngModel]="menuTheme()" inputId="light" (ngModelChange)="onMenuThemeChange('light')" [disabled]="menuMode() === 'horizontal' || darkTheme()"></p-radio-button>
                            <label for="light">Light</label>
                        </div>
                        <div class="flex items-center gap-2">
                            <p-radio-button name="menuTheme" value="dark" [ngModel]="menuTheme()" (ngModelChange)="onMenuThemeChange('dark')" inputId="dark" [disabled]="menuMode() === 'horizontal' || darkTheme()"></p-radio-button>
                            <label for="dark">Dark</label>
                        </div>
                    </div>
                </div> -->
            </div>
    `
})
export class AppConfigurator {
    @Input() simple: boolean = false;
    configurationService: ConfigurationService = inject(ConfigurationService);
    layoutService: LayoutService = inject(LayoutService);

    get visible() {
        return this.configurationService.visible;
    }

    get primaryColors() {
        return this.configurationService.primaryColors;
    }

    get selectedPrimaryColor() {
        return this.configurationService.selectedPrimaryColor;
    }

    get selectedSurfaceColor() {
        return this.configurationService.selectedSurfaceColor;
    }

    get darkTheme() {
        return this.configurationService.darkTheme;
    }

    menuMode = computed(() => this.layoutService.layoutConfig().menuMode);

    get menuTheme() {
        return this.configurationService.menuTheme;
    }

    get themeOptions() {
        return this.configurationService.themeOptions;
    }
    
    onMenuThemeChange(theme: string) {
        this.configurationService.onMenuThemeChange(theme);
    }

    getPresetExt() {
        return this.configurationService.getPresetExt();
    }

    updateColors(event: any, type: string, color: any) {
        this.configurationService.updateColors(event, type, color);
    }

    applyTheme(type: string, color: any) {
       this.configurationService.applyTheme(type, color);
    }

    onPresetChange(event: any) {
        this.configurationService.onPresetChange(event);
    }

    onDrawerHide() {
        this.configurationService.onDrawerHide();
    }

    onMenuModeChange(event: string) {
        this.configurationService.onMenuModeChange(event);
    }

    toggleDarkMode() {
        this.configurationService.toggleDarkMode();
    }

    executeDarkModeToggle() {
        this.configurationService.executeDarkModeToggle();
    }

    toggleConfigSidebar() {
        this.configurationService.toggleConfigSidebar();
    }
}
